
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

export default function HeroSection() {
  const [counts, setCounts] = useState({
    projects: 0,
    years: 0,
    satisfaction: 0,
    support: 0,
  });
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  useEffect(() => {
    if (!statsInView) return;

    const duration = 2000;
    const steps = 60;
    let current = 0;
    const timer = setInterval(() => {
      current++;
      setCounts({
        projects: Math.min(Math.floor((2350 / steps) * current), 2350),
        years: Math.min(Math.floor((15 / steps) * current), 15),
        satisfaction: Math.min(Math.floor((99 / steps) * current), 99),
        support: Math.min(Math.floor((24 / steps) * current), 24),
      });
      if (current >= steps) clearInterval(timer);
    }, duration / steps);

    return () => clearInterval(timer);
  }, [statsInView]);

  // Helper to format the satisfaction value safely
  const formatSatisfaction = (value) => {
    // Ensure we always get a number and format as "XX.8%" (e.g., "99.8%")
    const num = Number(value) || 0;
    return `${num}.8%`;
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/547bc830067e3657ad2f788dc2e086fa.jpg"
          alt="Yangın Algılama ve İhbar Sistemleri"
          title="Yangın algılama sistemleri kurumsal görsel"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-white">7/24 Acil Müdahale Hizmeti</span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Yangın Güvenliğinde
              <span className="block text-red-500 mt-2">Türkiye'nin Güvenilir Çözüm Ortağı</span>
            </h1>

            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
              CE, TSE ve ISO 9001 sertifikalı yangın algılama sistemleri ile can ve mal güvenliğinizi en üst seviyede koruyoruz. 15 yılı aşkın deneyimimizle yanınızdayız.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/hizmetler"
                className="px-8 py-4 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-arrow-right-line"></i>
                Hizmetlerimiz
              </Link>
              <a
                href="#iletisim"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Bize Ulaşın
              </a>
            </div>
          </motion.div>

          <motion.div
            ref={statsRef}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="grid grid-cols-2 gap-5">
              {[
                {
                  value: `${counts.projects}+`,
                  label: 'Tamamlanan Proje',
                  icon: 'ri-building-2-line',
                },
                { value: `${counts.years}+`, label: 'Yıllık Deneyim', icon: 'ri-time-line' },
                {
                  // Fixed the syntax error: value must be a valid string expression
                  value: formatSatisfaction(counts.satisfaction),
                  label: 'Müşteri Memnuniyeti',
                  icon: 'ri-heart-line',
                },
                { value: `${counts.support}/7`, label: 'Teknik Destek', icon: 'ri-customer-service-line' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.15 }}
                  className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-6 text-center hover:bg-white/15 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-red-500/20 rounded-xl mx-auto mb-3">
                    <i className={`${stat.icon} text-xl text-red-400`}></i>
                  </div>
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-sm text-white/60 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-white/60 rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
}
